import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { useMemo } from 'react';
import type { TFunction } from 'i18next';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast';
import { ApiError, request } from '@/lib/api';
import type { InventoryItem } from '@/lib/schemas';
import { cn, formatNumber } from '@/lib/utils';
import { INVENTORY_QUERY_KEY, stockRowSchema } from './inventory-api';

/* ---------------------------------------------------------------------------
   עדכון כמות לפריט בודד.

   `AdjustQuantityDto` מקבל `delta` (שלם, ≠ 0) ו-`note` אופציונלית בלבד.
   סוג התנועה — RESTOCK מול MANUAL_ADJUSTMENT — *נגזר בשרת מסימן ה-delta*
   ואינו שדה בגוף הבקשה. לכן הטופס שואל "כיוון + כמות" ולא שולח reason:
   בחירת הכיוון היא בחירת הסיבה, וכך אי אפשר לשלוח צירוף שהשרת יסתור.
   --------------------------------------------------------------------------- */

const MAX_DELTA = 1_000_000;

function buildSchema(t: TFunction<['inventory', 'common']>, currentQuantity: number) {
  return z
    .object({
      direction: z.enum(['add', 'remove']),
      amount: z
        .string()
        .trim()
        .min(1, t('adjust.amountRequired'))
        .regex(/^\d+$/, t('adjust.amountInteger'))
        .refine((v) => Number(v) > 0, t('adjust.amountPositive'))
        .refine((v) => Number(v) <= MAX_DELTA, t('adjust.amountMax', { max: formatNumber(MAX_DELTA) })),
      note: z.string().trim().max(500, t('adjust.noteMax')).optional(),
    })
    .superRefine((values, ctx) => {
      const amount = Number(values.amount);
      if (!Number.isFinite(amount)) return;
      // חסימה בצד הלקוח כדי לא לשלוח בקשה שנדחית ממילא; השרת חוסם
      // את אותו מקרה ב-409, וגם בו מטופל — ראו onError.
      if (values.direction === 'remove' && amount > currentQuantity) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['amount'],
          message: t('adjust.belowZero', {
            amount: formatNumber(amount),
            current: formatNumber(currentQuantity),
          }),
        });
      }
    });
}

type AdjustForm = z.infer<ReturnType<typeof buildSchema>>;

export function AdjustQuantityDialog({
  item,
  onClose,
}: {
  item: InventoryItem;
  onClose: () => void;
}) {
  const { t } = useTranslation(['inventory', 'common']);
  const toast = useToast();
  const queryClient = useQueryClient();

  const schema = useMemo(() => buildSchema(t, item.quantity), [t, item.quantity]);

  const form = useForm<AdjustForm>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    defaultValues: { direction: 'add', amount: '', note: '' },
  });

  const direction = form.watch('direction');
  const amountRaw = form.watch('amount');

  const parsedAmount = /^\d+$/.test(amountRaw.trim()) ? Number(amountRaw.trim()) : null;
  const delta = parsedAmount === null ? null : direction === 'add' ? parsedAmount : -parsedAmount;
  const preview = delta === null ? null : item.quantity + delta;
  const previewInvalid = preview !== null && preview < 0;

  const mutation = useMutation({
    mutationFn: (values: AdjustForm) => {
      const amount = Number(values.amount);
      const note = values.note?.trim();
      return request(`/inventory/${item.id}/adjust`, {
        method: 'PATCH',
        body: {
          delta: values.direction === 'add' ? amount : -amount,
          // מחרוזת ריקה נדחית בשרת (Length 1..500) — לא שולחים שדה ריק.
          note: note && note.length > 0 ? note : undefined,
        },
        schema: stockRowSchema,
      });
    },
    onSuccess: (row) => {
      void queryClient.invalidateQueries({ queryKey: [INVENTORY_QUERY_KEY] });
      toast.success(
        t('adjust.updated'),
        t('adjust.updatedDetail', { name: row.name, quantity: formatNumber(row.quantity) }),
      );
      onClose();
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.status === 409) {
        // הכמות בשרת השתנתה מאז שהמסך נטען. הודעת השרת היא האמת,
        // והרענון מחזיר את השדה לתצוגת המצב האמיתי.
        void queryClient.invalidateQueries({ queryKey: [INVENTORY_QUERY_KEY] });
        form.setError('amount', {
          type: 'server',
          message: t('adjust.conflict'),
        });
        toast.error(t('adjust.rejected'), error.message);
        return;
      }
      toast.error(
        t('adjust.failed'),
        error instanceof ApiError ? error.message : t('adjust.tryAgainShortly'),
      );
    },
  });

  const onSubmit = form.handleSubmit((values) => mutation.mutate(values));

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent aria-describedby="adjust-desc">
        <DialogHeader>
          <DialogTitle>{t('adjust.title')}</DialogTitle>
          <DialogDescription id="adjust-desc">
            {item.name} · <span className="ltr-inline">{item.sku}</span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void onSubmit(e)} noValidate>
          <DialogBody>
            <div className="flex flex-wrap items-center gap-3 rounded-(--radius-md) border border-border bg-surface-sunken px-4 py-3">
              <div>
                <p className="text-2xs text-fg-subtle">{t('adjust.current')}</p>
                <p className="tabular text-sm font-semibold text-fg">
                  {formatNumber(item.quantity)}
                </p>
              </div>
              <div
                className="ms-auto text-end"
                aria-live="polite"
                // התצוגה מתעדכנת תוך כדי הקלדה; polite כדי שקורא מסך
                // יכריז על התוצאה בלי לקטוע את ההקלדה.
              >
                <p className="text-2xs text-fg-subtle">{t('adjust.after')}</p>
                <p
                  className={cn(
                    'tabular text-sm font-semibold',
                    preview === null
                      ? 'text-fg-subtle'
                      : previewInvalid
                        ? 'text-danger'
                        : 'text-fg',
                  )}
                >
                  {preview === null ? '—' : formatNumber(preview)}
                </p>
              </div>
            </div>

            <Field label={t('adjust.type')} htmlFor="adjust-direction" required>
              <Controller
                control={form.control}
                name="direction"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(v) => field.onChange(v)}
                  >
                    <SelectTrigger id="adjust-direction" aria-label={t('adjust.type')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="add">{t('adjust.typeAdd')}</SelectItem>
                      <SelectItem value="remove">{t('adjust.typeRemove')}</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>

            <Field
              label={t('adjust.amount')}
              htmlFor="adjust-amount"
              required
              error={form.formState.errors.amount?.message}
              hint={
                direction === 'add' ? t('adjust.hintAdd') : t('adjust.hintRemove')
              }
            >
              <div className="relative">
                {direction === 'add' ? (
                  <ArrowUpRight
                    className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-success"
                    aria-hidden
                  />
                ) : (
                  <ArrowDownRight
                    className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-danger"
                    aria-hidden
                  />
                )}
                <Input
                  id="adjust-amount"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  autoFocus
                  className="ps-9"
                  invalid={Boolean(form.formState.errors.amount)}
                  aria-describedby={
                    form.formState.errors.amount ? 'adjust-amount-error' : 'adjust-amount-hint'
                  }
                  {...form.register('amount')}
                />
              </div>
            </Field>

            <Field
              label={t('adjust.note')}
              htmlFor="adjust-note"
              error={form.formState.errors.note?.message}
              hint={t('adjust.noteHint')}
            >
              <Textarea
                id="adjust-note"
                rows={3}
                maxLength={500}
                invalid={Boolean(form.formState.errors.note)}
                aria-describedby={
                  form.formState.errors.note ? 'adjust-note-error' : 'adjust-note-hint'
                }
                {...form.register('note')}
              />
            </Field>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending} disabled={previewInvalid}>
              {t('adjust.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
